import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AppFooter } from '@/components/AppFooter';
import { HeroVisual } from './HeroVisual';
import { LandingFeatures } from './LandingFeatures';
import { LandingHowItWorks } from './LandingHowItWorks';
import { LandingNav } from './LandingNav';
import { landing, landingGlow, landingGradient } from './landingTheme';

/**
 * The public page somebody lands on before they have signed in.
 *
 * @remarks
 * <b>Every claim here is one the product can keep.</b> The design it follows was drawn with an
 * assistant that led on artificial intelligence, complete with a mascot; this product has no
 * such feature, so the headline is not that. What replaced it is what the product actually has
 * and what the system of record does not: planning above the task, dates, dependencies,
 * capacity, and a Persian calendar throughout.
 *
 * <b>The vocabulary firewall holds here too</b> (non-negotiable 7). A landing page is read by
 * exactly the people that rule protects, so the words <em>issue</em>, <em>milestone</em> and
 * <em>repository</em> do not appear - the system of record is named the way every screen in
 * the product already names it.
 *
 * Rendered at the root for anybody without a session. Somebody signed in goes to their work
 * instead, which is the screen they came for.
 */
export function LandingPage() {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: landing.base,
        color: landing.text,

        // The glow is painted on the page rather than on a section, so the hero and the
        // features below it sit in one field of light instead of two.
        backgroundImage: landingGlow,
        backgroundRepeat: 'no-repeat',
      }}
    >
      <LandingNav />

      <Box component="main" sx={{ flex: 1 }}>
        <Hero />
        <LandingFeatures />
        <LandingHowItWorks />
      </Box>

      <AppFooter />
    </Box>
  );
}

function Hero() {
  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 8, md: 14 } }}>
      <Stack
        direction={{ xs: 'column-reverse', md: 'row' }}
        spacing={{ xs: 8, md: 6 }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <HeroVisual />
        </Box>

        <Stack spacing={3} sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'center', md: 'start' } }}>
          <Badge />

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 38, sm: 50, md: 60 },
              fontWeight: 800,
              lineHeight: 1.18,
              letterSpacing: '-0.02em',
            }}
          >
            سامانه کنترل وظایف
            <Box
              component="span"
              sx={{
                display: 'block',
                background: landingGradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              رای‌ویرا
            </Box>
          </Typography>

          <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 700, color: landing.text }}>
            برنامه‌ریزی، پیگیری و گزارش‌گیری پروژه‌ها، فازها و وظایف
          </Typography>

          <Typography sx={{ fontSize: { xs: 15, md: 16 }, color: landing.textMuted, lineHeight: 2, maxWidth: 520 }}>
            کار تیم شما همان‌جایی می‌ماند که هست؛ رای‌ویرا چیزی را اضافه می‌کند که آنجا وجود
            ندارد: تاریخ شروع، وابستگی میان کارها، ظرفیت افراد و نمای یکپارچهٔ همهٔ پروژه‌ها.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ pt: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}
          >
            <Button
              component={RouterLink}
              to="/sign-in"
              size="large"
              sx={{
                background: landingGradient,
                color: '#FFFFFF',
                fontWeight: 800,
                px: 4,
                py: 1.4,
                borderRadius: 99,
                boxShadow: '0 18px 40px -14px rgba(124, 92, 255, 0.8)',
                '&:hover': { background: landingGradient, filter: 'brightness(1.08)' },
              }}
            >
              ورود به سامانه
            </Button>

            <Button
              href="#features"
              size="large"
              sx={{
                px: 4,
                py: 1.4,
                borderRadius: 99,
                fontWeight: 700,
                color: landing.text,
                border: 1,
                borderColor: landing.borderBright,
                '&:hover': { borderColor: landing.violetSoft, backgroundColor: 'rgba(124, 92, 255, 0.08)' },
              }}
            >
              مشاهده ویژگی‌ها
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
}

/** The pill above the headline. It names the product, which is what somebody needs first. */
function Badge() {
  return (
    <Box
      sx={{
        alignSelf: { xs: 'center', md: 'flex-start' },
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 0.75,
        borderRadius: 99,
        border: 1,
        borderColor: landing.borderBright,
        backgroundColor: 'rgba(124, 92, 255, 0.10)',
        fontSize: 13,
        color: landing.violetSoft,
        fontWeight: 600,
      }}
    >
      سامانه کنترل وظایف رای‌ویرا
      <Box component="span" aria-hidden sx={{ fontSize: 15, lineHeight: 1 }}>
        ←
      </Box>
    </Box>
  );
}
